<template>
  <div
    v-if="isAuthenticated && loaded"
    id="app"
  >
    <PageNav />

    <Overlay class="MainContent">
      <div
        v-if="isGuest"
        class="mx-auto mt-5"
        style="width: 325px"
      >
        <h4>U heeft geen toegang</h4>
        <p>
          Uw gebruikersaccount heeft geen rechten toegewezen gekregen in de Workflow tool.
        </p>
        <p>
          Onze support afdeling kan u verder helpen indien dit onterecht is.
        </p>
        <p>
          <MailtoSupport />
        </p>
      </div>
      <div v-else>
        <router-view
          id="scroll-container"
          :key="routerKey"
        />
        <!-- <Tour
          v-if="isTourAllowed && showTour"
          :show-tour-introduction="showTourIntroduction"
          @end-tour="showTour = false"
        /> -->
      </div>
    </Overlay>

    <DebugModal v-if="isDebugModalVisible" />

    <PageFooter />

    <!-- <SessionModal /> -->
  </div>

  <!-- Loading screen - loading data -->
  <div
    v-else-if="hasRolesAndAcceptedTerms"
    id="app"
    class="LoadingScreen"
  >
    <div class="MainContent">
      <div class="pt-4">
        <div
          class="m-auto"
          style="width: 160px"
        >
          <EVToolsLogo />
        </div>
        <b-card
          class="m-auto"
          style="width: 275px"
          title="EVtools Workflow"
          sub-title="De datasets worden ingeladen"
        >
          <div
            v-if="! criticalFailure"
            class="LoadingScreen__Status mt-3"
          >
            <div class="d-flex justify-content-between">
              <span>Configuratie...</span>
              <span
                v-if="loadingStatus.config"
                class="done"
              >Done</span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
            <div
              v-if="usesPagedLoader"
              class="d-flex justify-content-between"
            >
              <span>Aanvragen...</span>
              <span v-if="loadingStatus.pagedRequests.records !== 0">
                <span>{{ loadingStatus.pagedRequests.records }}</span>
              </span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
            <div
              v-else
              class="d-flex justify-content-between"
            >
              <span>Aanvragen...</span>
              <span
                v-if="loadingStatus.requests"
                class="done"
              >Done</span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
            <div
              v-if="usesPagedLoader"
              class="d-flex justify-content-between"
            >
              <span>Realisatie processen...</span>
              <span v-if="loadingStatus.pagedWorkflows.records !== 0">
                <span>{{ loadingStatus.pagedWorkflows.records }}</span>
              </span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
            <div
              v-else
              class="d-flex justify-content-between"
            >
              <span>Realisatie processen...</span>
              <span
                v-if="loadingStatus.workflows"
                class="done"
              >Done</span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
            <div
              v-if="! usesCBAPI"
              class="d-flex justify-content-between"
            >
              <span>Laadpaal datasets... </span>
              <span v-if="loadingStatus.municipalities.target !== 0">
                <span>{{ loadingStatus.municipalities.current }} / {{ loadingStatus.municipalities.target }}</span>
              </span>
              <b-spinner
                v-else
                variant="secondary"
                small
              />
            </div>
          </div>

          <div v-if="missingMunicipalities.length || missingWorkflows.length || missingRequests.length">
            <p class="pt-4">
              Het laden van {{ loadingStatus.municipalities.error + loadingStatus.pagedWorkflows.error + loadingStatus.pagedRequests.error }} dataset(s) is mislukt.
            </p>
            <small v-if="loadingStatus.municipalities.code.length"> ({{ loadingStatus.municipalities.code.join(', ') }})</small>
            <b-button
              v-if="! loading"
              variant="primary"
              @click="handleRetryLoadingDatasets"
            >
              Probeer deze opnieuw
            </b-button>
          </div>

          <div v-if="criticalFailure">
            <p class="pt-4">
              Het laden van de applicatie configuratie is mislukt. Herlaad de pagina opnieuw.
            </p>
            <p>
              Neem contact op met support indien dit probleem zich blijft voordoen.
            </p>
          </div>

          <template #footer>
            <div style="font-size: 0.9rem;">
              <b-icon
                icon="envelope"
                font-scale="0.9"
              /> <MailtoSupport class="card-link" />
            </div>
          </template>
        </b-card>
      </div>
    </div>
  </div>

  <!-- terms check -->
  <div
    v-else-if="rolesAreLoaded && ! hasAcceptedTerms"
    id="app"
    class="LoadingScreen"
  >
    <div class="MainContent">
      <div class="pt-4">
        <div
          class="m-auto"
          style="width: 160px"
        >
          <EVToolsLogo />
        </div>
        <b-card
          class="m-auto"
          style="width: 600px"
          title="Gebruikersvoorwaarden"
        >
          <TermsText
            ref="terms"
            @setVersion="termsVersion = $event"
          />
          <div class="text-right">
            <b-spinner
              v-if="termsStatus === 'saving'"
              variant="secondary"
              small
              class="mr-3"
            />
            <b-button
              variant="primary"
              :disabled="termsStatus !== 'pending'"
              @click="handleAccept"
            >
              Ik ga akkoord
            </b-button>
          </div>
          <template #footer>
            <div style="font-size: 0.9rem;">
              <b-icon
                icon="envelope"
                font-scale="0.9"
              /> <MailtoSupport class="card-link" />
            </div>
          </template>
        </b-card>
      </div>
    </div>
  </div>

  <!-- primary authication check -->
  <div
    v-else
    id="app"
    class="LoadingScreen"
  >
    <div class="MainContent">
      <div class="pt-4">
        <div
          class="m-auto"
          style="width: 160px"
        >
          <EVToolsLogo />
        </div>
        <b-card
          class="m-auto"
          style="width: 275px"
          title="EVtools Workflow"
          sub-title="Authorisatie controle"
        >
          <template #footer>
            <div style="font-size: 0.9rem;">
              <b-icon
                icon="envelope"
                font-scale="0.9"
              /> <MailtoSupport class="card-link" />
            </div>
          </template>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import EVToolsLogo from '@/components/EVToolsLogo.vue'
import PageFooter from '@/components/PageFooter.vue'
import PageNav from '@/components/PageNav.vue'
import Overlay from '@/components/Overlay.vue'
import TermsText, { VERSION } from '@/components/TermsText.vue'
// import SessionModal from '@/components/SessionModal.vue'
import DebugModal from '@/components/debugger/DebugModal.vue'
//import Tour from '@/components/Tour'
import MailtoSupport from '@/components/common/MailtoSupport.vue'

// import { staticGeocode } from '@/services/geocoder'
import { municipalityOptions, tour } from '@/config'

// import GeocoderCheck from '@/components/mixins/GeocoderCheck.vue'

export default {
  name: 'App',
  components: {
    EVToolsLogo,
    PageNav, PageFooter, Overlay, TermsText,
    // SessionModal
    DebugModal,
    //Tour,
    MailtoSupport,
  },
  // mixins: [GeocoderCheck],
  data() {
    return {
      tour,
      showTour: false,
      showTourIntroduction: true,
      criticalFailure: false,
      usesCBAPI: true,

      notificationsDurationInMS: 10000,

      termsStatus: 'pending',

      loading: false,
      loadingStatus: {
        config: false,
        requests: false,
        workflows: false,
        pagedRequests: {
          error: 0,
          target: 0,
          current: 0,
          records: 0,
        },
        pagedWorkflows: {
          error: 0,
          target: 0,
          current: 0,
          records: 0,
        },
        municipalities: {
          error: 0,
          target: 0,
          current: 0,
          code: [],
        },
      },
      missingMunicipalities: [],
      missingWorkflows: [],
      missingRequests: [],

      // Flag to indicate the connections between requests & RPs have been established
      establishedConnections: false,
    }
  },
  computed: {
    ...mapGetters(['loaded']),
    ...mapGetters('requests', [
      // 'requestsWithoutCoordinates',
      'hasRequests',
      'requestByUuid',
    ]),
    ...mapGetters('realisation', {
      'realisationprocesses': 'records',
      recordByUuid: 'recordByUuid',
    }),
    ...mapGetters('user', [
      'canAccessRequests',
      'canAccessRealisationProcesses',
      'isGuest',
      'roleDetailsMerged',
      'rolesAreLoaded',
      'securityCheck',
    ]),
    ...mapGetters('debug', ['isDebugModalVisible']),
    ...mapGetters('tenant', [
      'getTenant',
      'isCurrentTenant',
      'isMapLoaded',
    ]),
    ...mapGetters('synchronisation', [
      'getAtTimestamp',
    ]),
    isAuthenticated() {
      return this.$auth.isAuthenticated && this.$auth.user
    },
    userRolesAreAvailable() {
      return this.isAuthenticated && this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}roles`]
    },
    subTitle() {
      return this.userRolesAreAvailable
        ? 'De datasets worden ingeladen'
        : 'Authorisatie controle'
    },
    // TODO: CBAPI - TENANT CONFIG ?
    usesPagedLoader() {
      return this.isCurrentTenant({ tenant: 'go-ral' })
        || this.isCurrentTenant({ tenant: 'park-n-charge' })
        || this.isCurrentTenant({ tenant: 'opcharge' })
        || this.isCurrentTenant({ tenant: 'vlaanderen' })
    },
    hasAcceptedTerms() {
      return this.termsStatus === 'accepted'
    },
    // TODO: CBAPI - TENANT CONFIG
    isTourAllowed () {
      return ['opcharge', 'go-ral', 'park-n-charge', 'vlaanderen'].includes(process.env.VUE_APP_TENANT)
    },
    hasRolesAndAcceptedTerms() {
      return this.rolesAreLoaded && this.hasAcceptedTerms
    },
    routerKey () {
      return this.$route.name === 'RequestDetail' ? this.$route.fullPath : null
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler (routeName) {
        this.handleTour ({ routeName })
      },
    },
    userRolesAreAvailable(value) {
      if (value) {
        this.init()
      }
    },

    loaded(value) {
      // Trigger Geocoder for requests without coordinates
      if (value === true) {

        // Check the Aanvragen data
        // this.runGeocoder()

        // Update the RP data that depends on connected Location (e.g. GO-RAL)
        this.updateAddressData()
        this.handleTour ({ routeName: this.$route.name })
      }
    },
    hasRolesAndAcceptedTerms(value) {
      if (value === true) {
        this.$nextTick(this.loadData)
      }
    },
  },
  created() {
    // The first sync ts is used for `at` collection queries during data sync (e.g. while loading)
    this.setFirstSyncTimestamp()
  },
  methods: {
    ...mapMutations('synchronisation', [
      'setFirstSyncTimestamp',
      'setFromTimestamp',
    ]),
    ...mapActions('tenant', [
      'loadConfig',
      'loadWorkflowSpecs',
    ]),
    ...mapActions('stakeholders', [
      'loadStakeholders',
    ]),
    ...mapActions('realisation', {
      'loadRealisations': 'loadRecords',
      'loadPagedRealisationByCode': 'loadRecordsByCode',
      'loadProcessRecordsFromCBAPI': 'loadProcessRecordsFromCBAPI',
    }),
    ...mapActions('requests', [
      'loadRequests',
      'loadRequestsByCode',
      'loadRequestRecordsFromCBAPI',
    ]),
    ...mapMutations([
      'setLoadedState',
    ]),
    ...mapActions('chargingpoints', [
      'loadLocationRecordsFromCBAPI',
    ]),
    ...mapMutations('relations', [
      'addConnection',
    ]),
    ...mapMutations('user', [
      'setRoles',
    ]),
    init() {
      if (this.loading || this.loaded) return

      this.loading = true

      this.checkTerms()

      this.loadRoles()
      this.handleTourAccess()
    },


    loadRoles() {
      let roles = this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}roles`] || {}

      // Older users have only one set of roles, new users have roles per tenant
      if (Array.isArray(roles)) {
        roles = roles.find(role => role.tenant === process.env.VUE_APP_TENANT)
      }

      if (! roles) {
        roles = {
          tenant: process.env.VUE_APP_TENANT,
          admin: [],
          cpo: [],
          municipality: [],
        }
      }

      this.setRoles({
        roles,
      })
    },

    handleTour ({ routeName }) {
      const tourStorage = Object.keys({ ...localStorage })
      this.showTourIntroduction = !tourStorage.some(key => key.includes(tour.date))

      // When History api back/forth is used, tour overlay keeps hanging so we need to remove the class manually //
      document.body.classList.remove('v-tour--active')
      // Check if some Realisation.Step route is already seen
      const realisationStep = 'Realisation.Step'
      const isStepTourSeen = tourStorage.some(key => key.startsWith(realisationStep))
      this.showTour = false

      // Don't initiate the tour if realisation type needs to be selected first //
      if (routeName?.startsWith(realisationStep) && !this.recordByUuid({ uuid: this.$route.params.uuid })) return

      if (this.tour?.[routeName]) {

        this.$nextTick(() => {
          const steps = tour[routeName].steps
          const isRealisationStepSeen = routeName?.startsWith(realisationStep) && isStepTourSeen

          if (isRealisationStepSeen) {
            // If one of the Realisation.Step routes is already seen, then remove the whole tour //
            // or only the last step in the tour if more are present, to prevent repetition on other Realisation.Step tours //
            // if other unseen steps need to be seen

            if (steps?.length === 1 && steps[0].isOnAllSteps) {
              delete tour[routeName]
            } else {
              steps.map((step, index) => {
                if (step.isOnAllSteps) {
                  delete tour[routeName].steps[index]
                }
              })
            }
          }
          this.showTour = !!tour[routeName]
        })
      }
    },

    handleTourAccess () {
      Object.keys(tour).map(route => {
        const feature = tour[route].access
        if (feature && !this.securityCheck({ attribute: feature })) {
          // Remove whole tour if user has no access to all features it presents //
          return delete tour[route]
        }

        tour[route].steps?.map((step, stepIndex) => {
          if (step.access && !this.securityCheck({ attribute: step.access })) {
            // Remove individual step if user has no access to a feature it presents //
            delete tour[route].steps[stepIndex]
          }
        })
      })
    },

    checkTerms: function() {
      const terms = this.$auth.user[`${process.env.VUE_APP_AUTH_NAMESPACE}terms`] || {}
      const version = VERSION.replaceAll('_', '.')

      if (Object.keys(terms).includes(version)) {
        this.termsStatus = 'accepted'
      }
    },

    handleAccept: async function() {
      try {
        this.termsStatus = 'saving'
        const response = await this.$cbapi.user.acceptTerms({
          version: VERSION,
        })
        if (response !== false) {
          this.termsStatus = 'accepted'
        } else {
          throw new Error('Something went wrong with accepting terms')
        }
      } catch (e) {
        console.error(e)
      }
    },


    loadData: async function() {
      const token = await this.$auth.getTokenSilently()

      try {
        // We need the tenant config & workflow specs while processing the other data
        await this.loadConfig({ token })
        this.setPageTitle()

        await Promise.all([
          this.loadWorkflowSpecs(),
          this.loadStakeholders(),
        ])

        this.loadingStatus.config = true

      } catch (e) {
        this.criticalFailure = true

        return // Kill the loading process. Game Over.
      }


      if (this.usesPagedLoader) {
        await Promise.all([
          this.loadPagedRequests(),
          this.loadPagedRealisations({ token }),
          // this.loadChargingPoints()
        ])

        // Update the sync from timestamp with the ts we just used as `at` timestamp
        this.setFromTimestamp({ ts: this.getAtTimestamp })

      } else {

        await this.loadRequests({ token }).then(() => {
          this.loadingStatus.requests = true
        })
        await this.loadRealisations({ token }).then(() => {
          this.loadingStatus.workflows = true
        })
        await this.loadChargingPoints()
      }

      if (
        this.missingWorkflows.length === 0
        && this.missingRequests.length === 0
      ) {

        // If there were no requests with the per municipality approach, try the old method
        // This is currently necessary for PnC, which is missing a municipality reference.
        if (! this.hasRequests) {
          await this.loadRequests({ token })
        }

        this.matchRequestToRealisations()
        this.establishedConnections = true
      }

      if (
        this.missingMunicipalities.length === 0
        && this.missingWorkflows.length === 0
        && this.missingRequests.length === 0
      ) {


        this.setLoadedState({ loaded: true })
      }
      this.loading = false
    },


    /**
     * Go through the realisation process objects and connect the request objects
     */
    matchRequestToRealisations() {
      if (! this.canAccessRequests && ! this.canAccessRealisationProcesses) return
      // if (! this.isMunicipality) return

      (this.realisationprocesses || []).forEach(record => {
        (record.requestUuids || []).forEach(uuid => {
          this.addConnection({
            requestUuid: uuid,
            realisationUuid: record.uuid,
          })
        })
      })
    },


    async loadPagedRequests() {
      let codes = municipalityOptions
        .filter(muni => muni.disabled === false)
        .map(muni => muni.value)

      if (! this.roleDetailsMerged.includes('*')) {
        codes = codes.filter(code => this.roleDetailsMerged.includes(code))
      }

      this.loadingStatus.pagedRequests.target = 1

      return await this.loadRequestRecordsFromCBAPI({
        codes,
        progressCallback: this.updatePagedRequestsCounter,
        at: this.getAtTimestamp,
      })
    },

    async loadPagedRealisations() {
      let codes = municipalityOptions
        .filter(muni => muni.disabled === false)
        .map(muni => muni.value)

      if (! this.roleDetailsMerged.includes('*')) {
        codes = codes.filter(code => this.roleDetailsMerged.includes(code))
      }

      // TODO: CBAPI - LOAD - PRIO 2 - remove target & count records instead
      this.loadingStatus.pagedWorkflows.target = 1

      return await this.loadProcessRecordsFromCBAPI({
        codes,
        progressCallback: this.updatePagedWorkflowsCounter,
      })
    },
    updatePagedRequestsCounter({ count }) {
      this.loadingStatus.pagedRequests.current++
      this.loadingStatus.pagedRequests.records = count
    },
    updatePagedWorkflowsCounter({ count }) {
      this.loadingStatus.pagedWorkflows.current++
      this.loadingStatus.pagedWorkflows.records = count
    },
    async loadChargingPoints() {
      let codes = municipalityOptions
        .filter(muni => muni.disabled === false)
        .map(muni => muni.value)

      if (! this.roleDetailsMerged.includes('*')) {
        codes = codes.filter(code => this.roleDetailsMerged.includes(code))
      }

      // codes = [codes[0]]
      // TODO: CBAPI - TEMP HARDCODED
      codes = ['0281']

      this.loadingStatus.municipalities.target = 1

      return await this.loadLocationRecordsFromCBAPI({
        codes,
        progressCallback: () => {
          this.loadingStatus.municipalities.current++
        },
      })
    },



    async loadPagedRequestsOld({ token }) {
      let codes = municipalityOptions
        .filter(muni => muni.disabled === false)
        .map(muni => muni.value)

      if (! this.roleDetailsMerged.includes('*')) {
        codes = codes.filter(code => this.roleDetailsMerged.includes(code))
      }

      this.loadingStatus.pagedRequests.target = codes.length

      await Promise.all(
        codes.map(code => {
          return this.loadRequestsByCode({ token, code })
            .then(() => {
              this.loadingStatus.pagedRequests.current++
            })
            .catch(() => {
              this.loadingStatus.pagedRequests.error++
              this.missingRequests.push(code)
            })
        }),
      )
    },
    async loadPagedRealisationsOld({ token }) {
      let codes = municipalityOptions
        .filter(muni => muni.disabled === false)
        .map(muni => muni.value)

      if (! this.roleDetailsMerged.includes('*')) {
        codes = codes.filter(code => this.roleDetailsMerged.includes(code))
      }

      codes = [codes[0]]

      this.loadingStatus.pagedWorkflows.target = codes.length

      await Promise.all(
        codes.map(code => {
          return this.loadPagedRealisationByCode({ token, code })
            .then(() => {
              this.loadingStatus.pagedWorkflows.current++
            })
            .catch(() => {
              this.loadingStatus.pagedWorkflows.error++
              this.missingWorkflows.push(code)
            })
        }),
      )
    },


    /**
     *
     */
    async handleRetryLoadingDatasets() {
      await this.handleRetryRequests()
      await this.handleRetryWorkflows()

      if (
        this.establishedConnections === false
        && this.missingWorkflows.length === 0
        && this.missingRequests.length === 0
      ) {
        this.matchRequestToRealisations()
        this.establishedConnections = true
      }

      if (
        this.missingMunicipalities.length === 0
        && this.missingWorkflows.length === 0
        && this.missingRequests.length === 0
      ) {
        this.setLoadedState({ loaded: true })
      }
    },

    async handleRetryWorkflows() {
      const token = await this.$auth.getTokenSilently()
      this.loading = true
      let failed = []

      await Promise.all(
        this.missingWorkflows.map(code => {
          return this.loadPagedRealisationByCode({ token, code })
            .then(() => {
              this.loadingStatus.pagedWorkflows.current++
              this.loadingStatus.pagedWorkflows.error--
            })
            .catch(() => {
              failed.push(code)
            })
        }),
      )
      this.missingWorkflows = failed
      this.loading = false
    },

    async handleRetryRequests() {
      const token = await this.$auth.getTokenSilently()
      this.loading = true
      let failed = []

      await Promise.all(
        this.missingRequests.map(code => {
          return this.loadRequestsByCode({ token, code })
            .then(() => {
              this.loadingStatus.pagedRequests.current++
              this.loadingStatus.pagedRequests.error--
            })
            .catch(() => {
              failed.push(code)
            })
        }),
      )
      this.missingRequests = failed
      this.loading = false
    },



    updateAddressData() {
      this.realisationprocesses.forEach(record => {
        record.address = record.generateAddressData({ model: record })
      })
    },

    /**
     * Set the page title (seen in browser tabs)
     */
    setPageTitle() {
      let title = null
      switch (this.getTenant) {
        case 'rvb':
          title = 'RVB - Realisatieportaal'
          break
      }

      if (title) {
        document.title = title
      }
    },

  },
}
</script>

<style lang="scss">
html, body {
  // height: 100%;
  margin: 0;
  padding: 0;
}
html {
  overflow-y: scroll;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #2c3e50;
  padding: 0;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100%;
  height: 100%;
  max-height: 100%;
}
.MainContent {
  // overflow-y: scroll;
  min-height: 100vh;
  padding-top: 52px; // 52px offset for the header
  padding-bottom: 25px; // 25px offset for footer
}
.LoadingScreen {
  background: #041E42;

  .card {
    box-shadow: 0 0 40px 4px #111118;
  }

  &__Status .done {
    color: #00B46B;
  }
}

.v-tour--active {
  .DetailGrid__sidebar {
    position: relative;
    z-index: 3;
  }
  .v-step__header__close {
    font-size: 16px;
    margin: 0;
    right: -0.8em;
    top: -0.1em;
    position: relative;
  }
}
.v-tour__target--highlighted {
  padding:1em;
  legend {
    > span {
      display: inline-block;
      position: relative;
      top: 1em;
    }
  }
}
.Reporting + div {
  .v-tour {
    .v-step {
      max-width: 500px;
      min-width: 400px;
      .v-step__header {
        width: auto;
      }
    }
  }
}

.vue-notification-group {
  top: 68px !important;
  right: 10px !important;
}
.b-toaster.b-toaster-top-right {
  top: 64px !important;
}
</style>
